import { defineStore } from "pinia";
import { useUtility } from "@/composables/useUtility";

import { Tag } from "@/api/models/Person/Tag";
import { tagMapper } from "@/api/mappers/TagMapper";
import tagData from "@/api/mock/tags.json"; // temporary mock data

import { MembershipType } from "@/api/models/MembershipType";
import { membershipTypeMapper } from "@/api/mappers/MembershipTypeMapper";
import membshipTypeData from "@/api/mock/membershipTypes.json"; // temporary mock data

import { ProgramType } from "@/api/models/ProgramType";
import { programTypeMapper } from "@/api/mappers/ProgramTypeMapper";
import programTypesData from "@/api/mock/programTypes.json"; // temporary mock data

import { MembershipTypeDiscount } from "@/api/models/MembershipTypeDiscount";
import { membershipTypeDiscountMapper } from "@/api/mappers/MembershipTypeDiscountMapper";
import membershipTypeDiscountData from "@/api/mock/membershipTypeDiscounts.json"; // temporary mock data

import { Rating } from "@/api/models/Rating";
import { ratingMapper } from "@/api/mappers/RatingMapper";
import ratingData from "@/api/mock/ratings.json"; // temporary mock data

import { RatingProgramMap } from "@/api/models/RatingProgramMap";
import { ratingsProgramsMapper } from "@/api/mappers/RatingsProgramsMapper";
import ratingsProgramsData from "@/api/mock/ratingsPrograms.json";

import { BoatType } from "@/api/models/BoatType";
import { boatMapper } from "@/api/mappers/BoatMapper";
import boatData from "@/api/mock/boatTypes.json"; // temporary mock data

import { PersonType } from "@/api/models/Person/PersonType";
import { personTypeMapper } from "@/api/mappers/PersonTypeMapper";
import personTypeData from "@/api/mock/personTypes.json"; // temporary mock data

import { Country } from "@/api/models/Country";
import { countryMapper } from "@/api/mappers/CountryMapper";
import countryData from "@/api/mock/countries.json"; // temporary mock data

import { AmericanState } from "@/api/models/AmericanState";
import { americanStateMapper } from "@/api/mappers/AmericanStateMapper";
import usStateData from "@/api/mock/states.json"; // temporary mock data

import { SwimProof } from "@/api/models/SwimProof";
import { swimProofMapper } from "@/api/mappers/SwimProofMapper";
import swimProofData from "@/api/mock/swimProofMethods.json"; // temporary mock data

import { DonationFund } from "@/api/models/DonationFunds";
import { donationFundsMapper } from "@/api/mappers/DonationFundsMapper";
import donationFundsData from "@/api/mock/donationFunds.json"; // temporary mock data

import { ApClassType } from "@/api/models/Classes/ApClassType";
import { apClassTypesMapper } from "@/api/mappers/ApClassTypeMapper";
import apClassTypesData from "@/api/mock/apClassTypes.json"; // temporary mock data

import signupTypeData from "@/api/mock/signupTypes.json";
import signOutTypeData from "@/api/mock/signOutTypes.json";
import { useClient } from "@/composables/useClient";

import { JpClassSkills } from "@/api/models/JpClassSkills";
import { jpClassSkillsMapper } from "@/api/mappers/JpClassSkillsMapper";
import { JpClassSkillsRatings } from "@/api/models/JpClassSkillsRatings";
import { jpClassSkillsRatingsMapper } from "@/api/mappers/JpClassSkillsRatingsMapper";
import { AxiosInstance } from "axios";

import { useLookupsAPI } from "@/composables/lookups/useLookupsAPI";
import { ClassSkill } from "@/api/models/Classes/ClassSkill";
import { ClassSkillsRating } from "@/api/models/Classes/ClassSkillsRating";

export type LookupTable = Record<string, string>;

/**
 * lookup store for app-wide lookup data
 * @todo Replace mock with API calls
 */
export const useLookupStore = defineStore("lookup", {
  state: () => ({
    lookupsLoaded: false,
    tags: [] as Tag[],
    personTypes: [] as PersonType[],
    membershipTypes: [] as MembershipType[],
    membershipTypeDiscounts: [] as MembershipTypeDiscount[],
    programTypes: [] as ProgramType[],
    boatTypes: [] as BoatType[],
    countries: [] as Country[],
    americanStates: [] as AmericanState[],
    swimProofMethods: [] as SwimProof[],
    donationFunds: [] as DonationFund[],
    signupTypes: {} as LookupTable,
    signOutTypes: {} as LookupTable,
    ratingsProgramMaps: [] as RatingProgramMap[],
    apClassTypes: [] as ApClassType[],

    ratingsLoaded: false,
    ratings: [] as Rating[],

    activeJpClassSkillsLoaded: false,
    activeJpClassSkills: [] as ClassSkill[],

    jpClassSkillsRatingsLoaded: false,
    jpClassSkillsRatings: new Map<number, ClassSkillsRating[]>(),
  }),

  getters: {},

  actions: {
    async loadLookups() {
      const { sortByName } = useUtility();
      const { fetchRatings, fetchJpClassSkills, fetchJpClassSkillsRatings } =
        useLookupsAPI();

      if (this.lookupsLoaded) {
        return;
      }
      this.tags = tagData.map((dto) =>
        tagMapper({ ...dto, tagName: dto.tagName ?? "" }),
      );
      this.personTypes = personTypeData.map(personTypeMapper);
      this.membershipTypes = membshipTypeData
        .map(membershipTypeMapper)
        .filter((mt): mt is MembershipType => mt.id !== null)
        .sort(sortByName);
      this.membershipTypeDiscounts = membershipTypeDiscountData.map(
        membershipTypeDiscountMapper,
      );
      this.programTypes = programTypesData
        .map(programTypeMapper)
        .sort(sortByName);
      // this.ratings = ;
      this.boatTypes = boatData.map(boatMapper);
      this.countries = countryData.map(countryMapper);
      this.americanStates = usStateData.map(americanStateMapper);
      this.swimProofMethods = swimProofData.map(swimProofMapper);
      this.donationFunds = donationFundsData.map(donationFundsMapper);
      this.signupTypes = signupTypeData as LookupTable;
      this.signOutTypes = signOutTypeData as LookupTable;
      this.ratingsProgramMaps = ratingsProgramsData.map(ratingsProgramsMapper);
      this.apClassTypes = apClassTypesData.map(apClassTypesMapper);

      // db calls in parallel
      // [this.ratings, this.activeJpClassSkills, this.jpClassSkillsRatings] =
      //   await Promise.all([
      //     fetchRatings(),
      //     fetchJpClassSkills(),
      //     fetchJpClassSkillsRatings(),
      //   ]);

      this.lookupsLoaded = true;
    },

    findPersonType(id: number): PersonType | null {
      return this.personTypes.find((item) => item.id === id) || null;
    },

    findTag(id: number): Tag | null {
      return this.tags.find((item) => item.id === id) || null;
    },

    findMembershipType(id: number): MembershipType | null {
      return this.membershipTypes.find((item) => item.id === id) || null;
    },

    findMembershipDiscountType(id: number): MembershipTypeDiscount | null {
      return (
        this.membershipTypeDiscounts.find((item) => item.id === id) || null
      );
    },

    findProgramType(id: number): ProgramType | null {
      return this.programTypes.find((item) => item.id === id) || null;
    },

    /**
     * @deprecated
     */
    findRating(id: number): Rating | null {
      return this.ratings.find((item) => item.id === id) || null;
    },

    findBoatType(id: number): BoatType | null {
      return this.boatTypes.find((item) => item.id === id) || null;
    },

    findDonationFund(id: number): DonationFund | null {
      return this.donationFunds.find((item) => item.id === id) || null;
    },

    findSignupType(token: string): string | undefined {
      return this.signupTypes[token];
    },

    findSignOutType(token: string): string | undefined {
      return this.signOutTypes[token];
    },
  },
});
